import { ReactNode, useCallback } from 'react';
import {
  MdOutlineAccessTime,
  MdOutlineCalendarToday,
  MdOutlineClear,
  MdOutlinePhotoFilter,
  MdOutlineReplay,
  MdWarning,
} from 'react-icons/md';
import { Button, Spinner, Tooltip, useDisclosure } from '@ds';
import { SchedulerConfirmationModal } from '@lp/features/Scheduler/components';
import { SchedulerMutationActions } from '@lp/features/Scheduler/types';
import { useScheduleMutationsLoader } from '@lp/features/Scheduler/hooks';
import { getIsAutogenerated, getIsValidatable } from '@lp/features/Scheduler/utils';
import {
  PlanningProcess,
  PlanningProcessStatus,
  ScheduleFillingState,
  SchedulePublishReadiness,
} from '@/api/apis/resources/schedule';
import { formatDate } from '@/utils/helpers';
import { AutogenerateInfo } from './AutogenerationInfo';

type ControlButtonsProps = {
  startDay: Date;
  actions: SchedulerMutationActions;
  disableAutogeneration?: boolean;
  hidePlanningToggle?: boolean;
  isPlanningMode: boolean;
  canCreatePlanning?: boolean;
  planningData?: PlanningProcess;
  violationsCount?: number;
  validationIsFetching?: boolean;
  publishReadinessData?: SchedulePublishReadiness;
  fillingState?: ScheduleFillingState;
  conflictsButton?: ReactNode;
  publishBlockerTooltip?: ReactNode;
  onPlanningCreated: () => void;
  /** Window label override, e.g. "Period 9 · 2026". Falls back to "MMMM yyyy" when absent. */
  periodLabel?: string;
  /** Date-range suffix, e.g. "13 August – 9 September". Appended in parentheses when provided. */
  periodDateRange?: string | null;
};

export const ControlButtons = ({
  startDay,
  actions,
  disableAutogeneration,
  hidePlanningToggle,
  isPlanningMode,
  planningData,
  violationsCount,
  validationIsFetching,
  publishReadinessData,
  fillingState,
  conflictsButton,
  publishBlockerTooltip,
  canCreatePlanning,
  onPlanningCreated,
  periodLabel,
  periodDateRange,
}: ControlButtonsProps) => {
  const startPlanningModal = useDisclosure();
  const discardPlanningModal = useDisclosure();
  const generateScheduleModal = useDisclosure();
  const publishScheduleModal = useDisclosure();
  const publishTooltip = useDisclosure();
  const [isMutating] = useScheduleMutationsLoader();
  const hasViolations = !!violationsCount && getIsValidatable(fillingState);
  const isReadyToPublish = !!publishReadinessData?.isReadyToPublish;
  const hasPublishBlockers = !!publishReadinessData?.blockers?.length;

  const windowLabel = (() => {
    if (!periodLabel) return formatDate(startDay, 'MMMM yyyy');
    // Dates first, period number in brackets - matching the schedule header.
    if (periodDateRange) return `${periodDateRange} (${periodLabel})`;
    return periodLabel;
  })();

  const handlePlanningStart = useCallback(async () => {
    await actions.onPlanningStart();
    onPlanningCreated();
  }, [actions, onPlanningCreated]);

  const renderPublishIcon = () => {
    if (validationIsFetching) return <Spinner size="sm" color="default" />;
    if (hasPublishBlockers)
      return (
        <Tooltip
          showArrow
          isOpen={publishTooltip.isOpen}
          onOpenChange={publishTooltip.onOpenChange}
          placement="bottom-end"
          content={publishBlockerTooltip}
          className="border px-2.5 py-2"
        >
          <div>
            <MdWarning size={18} />
          </div>
        </Tooltip>
      );
    return undefined;
  };

  const renderButtons = () => {
    if (!isPlanningMode && canCreatePlanning) {
      return (
        <Button
          size="md"
          isDisabled={isMutating}
          endContent={<MdOutlineCalendarToday />}
          onPress={startPlanningModal.onOpen}
        >
          Start Planning
        </Button>
      );
    }

    if (!isPlanningMode || !planningData) return null;

    const isGenerating = planningData.status === PlanningProcessStatus.Generating;
    const isGenerated = !!planningData.autoSchedulerInfo;
    const showPlanningButtons = planningData.status === PlanningProcessStatus.Editing;
    const isGenerationSuccessful = getIsAutogenerated(planningData.autoSchedulerInfo);

    switch (true) {
      case isGenerating:
        return (
          <Button isDisabled size="md" variant="outline" endContent={<MdOutlineAccessTime />}>
            Generation Started
          </Button>
        );
      case showPlanningButtons:
        return (
          <>
            {!isGenerationSuccessful && <AutogenerateInfo planningData={planningData} />}
            {hasViolations && conflictsButton}
            {!hidePlanningToggle && (
              <Button
                size="md"
                variant="outline"
                color="danger"
                isDisabled={isMutating}
                endContent={<MdOutlineClear />}
                onPress={discardPlanningModal.onOpen}
              >
                Discard Planning
              </Button>
            )}
            {!disableAutogeneration && (
              <Button
                size="md"
                variant="outline"
                isDisabled={isMutating}
                endContent={isGenerated ? <MdOutlineReplay /> : <MdOutlinePhotoFilter />}
                onPress={generateScheduleModal.onOpen}
              >
                {isGenerated ? 'Regenerate' : 'Autogenerate'}
              </Button>
            )}
            <Button
              size="md"
              isDisabled={validationIsFetching || isMutating || !(isReadyToPublish || hasPublishBlockers)}
              endContent={renderPublishIcon()}
              onPress={isReadyToPublish ? publishScheduleModal.onOpen : publishTooltip.onOpenChange}
            >
              Publish Schedule
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {renderButtons()}
      <SchedulerConfirmationModal
        isOpen={startPlanningModal.isOpen}
        title="Start Planning"
        confirmText="Start Planning"
        onConfirm={handlePlanningStart}
        onClose={startPlanningModal.onClose}
      >
        The entire system will be put into planning mode, you will no longer be able to add shifts that will be
        displayed publicly
      </SchedulerConfirmationModal>
      <SchedulerConfirmationModal
        isOpen={discardPlanningModal.isOpen}
        title="Discard Planning?"
        confirmText="Discard Planning"
        discardText="Cancel"
        confirmButtonProps={{ color: 'danger' }}
        onConfirm={actions.onPlanningDiscard}
        onClose={discardPlanningModal.onClose}
      >
        The entire system will exit planning mode, all unpublished changes will be deleted irretrievably. Are you sure
        you want to discard planning?
      </SchedulerConfirmationModal>
      <SchedulerConfirmationModal
        isOpen={generateScheduleModal.isOpen}
        title="Generate a New Schedule?"
        confirmText="Generate Schedule"
        onConfirm={actions.onGenerationStart}
        onClose={generateScheduleModal.onClose}
      >
        <span>
          The system will generate the schedule for the <b>{windowLabel}</b>. The generating process might take up to 24
          hours
        </span>
      </SchedulerConfirmationModal>
      <SchedulerConfirmationModal
        isOpen={publishScheduleModal.isOpen}
        title={hasViolations ? 'Accept Warnings?' : 'Publish Schedule?'}
        confirmText={hasViolations ? 'Accept & Publish' : 'Publish Schedule'}
        onConfirm={actions.onPlanningPublish}
        onClose={publishScheduleModal.onClose}
      >
        {hasViolations
          ? `The schedule is not compliant with general rules and therefore contains a number of warnings (${violationsCount}). Are you sure you want to publish schedule?`
          : 'This operation can not be undone. Are you sure you want to publish schedule?'}
      </SchedulerConfirmationModal>
    </>
  );
};
